import React, { useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  StyledBackLink,
  TableCard,
  PaginationWrapper,
  FooterWrapper,
  StyledSectionContainerDiv,
} from "./index.styles";
import { Topbar } from "../catalogue/topbar/Topbar";
import { StyledHeaderDiv } from "../catalogue/index.styles";
import { LeftArrow } from "../assets/icons";
import { Status } from "../../newLayout/page/components/Status/Status";
import { Pagination } from "antd";
import { Table, TableHeader, HeaderColumn, TableRow, TableData } from "../components/table";
import { usePortalOrders } from "../hooks/usePortalOrders";
import { usePortalCart } from "../hooks/usePortalCart";
import { OrderSkeleton } from "./components/OrderSkeleton";
import { B2bFooter } from "../components/layout/footer";
import SortDropdown from "../components/table/SortDropdown";
import FilterDropdown from "../components/table/FilterDropdown";
import DateFilterDropdown from "./components/dateFilter/DateFilterDropdown";
import { UpDown } from "../../newLayout/assets/svgIcon/listView/up-down-arrow";
import { ReactComponent as FilterIcon } from "../../newLayout/assets/svgIcon/listView/filter.svg";
import { ReactComponent as CloseIcon } from "../../newLayout/assets/svgIcon/listView/Close.svg";
import { IconBtn, CloseFilterPill } from "../components/table/index.styles";
import {
  useGetPortalOrderSalesNosQuery,
  useGetPortalOrderPoRefsQuery,
  useGetPortalOrderProductCodesQuery,
  useGetPortalOrderProductNamesQuery,
  useGetPortalOrderStatusesQuery,
} from "../../../store/apiSlice/portalOrderApiSlice";
import {
  usePortalCurrencySymbol,
  usePortalFormattedAmount,
} from "../hooks/usePortalFormattedAmount";

const selectOptions = data => {
  const rows = Array.isArray(data) ? data : data?.results || [];
  return rows.map(item => ({
    id: item.id,
    value: item.id,
    label: item.name || item.id,
  }));
};

const ORDER_COLUMNS = [
  {
    key: "sales_no",
    label: "Sales No",
    sortKey: "sales_no",
    filterKey: "sales_nos",
    useSelectionQuery: useGetPortalOrderSalesNosQuery,
  },
  {
    key: "order_date",
    label: "Order Date",
    sortKey: "order_date",
    filterKey: "order_date",
    isDateFilter: true,
  },
  {
    key: "po_ref",
    label: "PO Ref",
    sortKey: "po_ref",
    filterKey: "po_refs",
    useSelectionQuery: useGetPortalOrderPoRefsQuery,
  },
  {
    key: "product_code",
    label: "Product",
    sortKey: "product_code",
    filterKey: "product_codes",
    useSelectionQuery: useGetPortalOrderProductCodesQuery,
  },
  {
    key: "product_name",
    label: "Product Name",
    sortKey: "product_name",
    filterKey: "product_names",
    useSelectionQuery: useGetPortalOrderProductNamesQuery,
  },
  {
    key: "sub_total",
    label: "Subtotal",
    sortKey: "sub_total",
    align: "rightAlign",
  },
  {
    key: "tax",
    label: "Tax",
    sortKey: "tax",
    align: "rightAlign",
  },
  {
    key: "total",
    label: "Total",
    sortKey: "total",
    align: "rightAlign",
  },
  {
    key: "status",
    label: "Status",
    sortKey: "status",
    filterKey: "statuses",
    useSelectionQuery: useGetPortalOrderStatusesQuery,
    align: "centerAlign",
  },
];

export const OrderHistory = () => {
  const navigate = useNavigate();
  const { subdomain } = useParams();
  const formatAmount = usePortalFormattedAmount();
  const currencySymbol = usePortalCurrencySymbol();

  // Load cart data for Topbar sync
  const { itemCount: cartItemCount, total: cartTotal } = usePortalCart();

  // Load dynamic order data
  const {
    orders,
    totalCount,
    isLoading,
    isFetching,
    currentPage,
    pageSize,
    handlePageChange,
    activeFilters,
    handleFilterApply,
    ordering,
    handleSortChange,
    searchValue,
    setSearchValue,
  } = usePortalOrders();

  const [openSort, setOpenSort] = useState(null);
  const [openFilter, setOpenFilter] = useState(null);

  // Fetch status choices to map codes to labels
  const { data: statusChoices } = useGetPortalOrderStatusesQuery();

  const statusMap = useMemo(() => {
    const map = {};
    const list = Array.isArray(statusChoices) ? statusChoices : statusChoices?.results || [];
    list.forEach(item => {
      if (item.id && item.name) {
        map[item.id] = item.name;
      }
    });
    return map;
  }, [statusChoices]);

  const formatStatus = useCallback(
    status => {
      if (!status) return "";
      if (statusMap[status]) return statusMap[status];
      return status
        .split("_")
        .map(word => {
          if (word === "pkl") return "Pick";
          if (word === "rel") return "Released";
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(" ");
    },
    [statusMap],
  );

  const handleFilterClear = useCallback(
    key => {
      handleFilterApply(key, []);
    },
    [handleFilterApply],
  );

  const renderHeaderCell = useCallback(
    col => {
      const isSortAscendingActive = ordering === col.sortKey;
      const isSortDescendingActive = ordering === `-${col.sortKey}`;
      const filterVal = col.filterKey ? activeFilters[col.filterKey] : null;
      const isFilterActive = col.isDateFilter
        ? filterVal && filterVal.start && filterVal.end
        : filterVal && filterVal.length > 0;

      return (
        <>
          {col.sortKey && (
            <>
              <IconBtn
                type="button"
                $active={isSortAscendingActive || isSortDescendingActive}
                onClick={() => {
                  setOpenSort(openSort === col.key ? null : col.key);
                  setOpenFilter(null);
                }}
                title="Sort"
              >
                <UpDown
                  ascending={isSortAscendingActive ? "#2060ea" : "#676879"}
                  descending={isSortDescendingActive ? "#2060ea" : "#676879"}
                />
              </IconBtn>
              <SortDropdown
                isOpen={openSort === col.key}
                onClose={() => setOpenSort(null)}
                sortKey={col.sortKey}
                activeOrdering={ordering}
                onSort={val => {
                  handleSortChange(val || "");
                  setOpenSort(null);
                }}
              />
            </>
          )}
          <span>{col.label}</span>
          {col.filterKey && (col.useSelectionQuery || col.isDateFilter) && (
            <>
              {isFilterActive ? (
                <CloseFilterPill
                  onClick={() => {
                    setOpenFilter(openFilter === col.key ? null : col.key);
                    setOpenSort(null);
                  }}
                  title="Filter details"
                >
                  {col.isDateFilter ? "1" : activeFilters[col.filterKey].length}
                  <CloseIcon
                    onClick={e => {
                      e.stopPropagation();
                      handleFilterClear(col.filterKey);
                      setOpenFilter(null);
                    }}
                    title="Clear filter"
                  />
                </CloseFilterPill>
              ) : (
                <IconBtn
                  type="button"
                  $active={isFilterActive}
                  onClick={() => {
                    setOpenFilter(openFilter === col.key ? null : col.key);
                    setOpenSort(null);
                  }}
                  title="Filter"
                >
                  <FilterIcon style={{ color: isFilterActive ? "#2060ea" : "#676879" }} />
                </IconBtn>
              )}
              {openFilter === col.key &&
                (col.isDateFilter ? (
                  <DateFilterDropdown
                    isOpen={true}
                    onClose={() => setOpenFilter(null)}
                    label={col.label}
                    activeValues={activeFilters[col.filterKey]}
                    onApply={values => {
                      handleFilterApply(col.filterKey, values);
                      setOpenFilter(null);
                    }}
                    onClear={() => {
                      handleFilterClear(col.filterKey);
                      setOpenFilter(null);
                    }}
                  />
                ) : (
                  <FilterDropdown
                    isOpen={true}
                    onClose={() => setOpenFilter(null)}
                    label={col.label}
                    useSelectionQuery={col.useSelectionQuery}
                    selectOptions={selectOptions}
                    activeValues={activeFilters[col.filterKey] || []}
                    onApply={values => {
                      handleFilterApply(col.filterKey, values);
                      setOpenFilter(null);
                    }}
                    onClear={() => {
                      handleFilterClear(col.filterKey);
                      setOpenFilter(null);
                    }}
                  />
                ))}
            </>
          )}
        </>
      );
    },
    [
      ordering,
      activeFilters,
      openSort,
      openFilter,
      handleSortChange,
      handleFilterApply,
      handleFilterClear,
    ],
  );

  const handleBackToCatalog = () => {
    navigate(`/portal/${subdomain}/catalog`);
  };

  return (
    <>
      <Topbar
        cartItemCount={cartItemCount}
        cartTotal={cartTotal}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        placeholder={"Search sales no, PO, product..."}
      />
      <StyledSectionContainerDiv>
        {/* Breadcrumbs link */}
        <StyledBackLink onClick={handleBackToCatalog}>
          <LeftArrow />
          <span>Back to Catalogue</span>
        </StyledBackLink>

        {/* Title */}
        <StyledHeaderDiv style={{ marginBottom: "20px" }}>Your Orders</StyledHeaderDiv>

        {/* Table Wrapper with borders and styling */}
        <TableCard style={{ opacity: isFetching && !isLoading ? 0.7 : 1 }}>
          <Table
            className="order-table"
            containerStyle={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
            }}
          >
            <TableHeader>
              {ORDER_COLUMNS.map(col => {
                const alignProp =
                  col.align === "centerAlign"
                    ? { centerAlign: true }
                    : col.align === "rightAlign"
                      ? { rightAlign: true }
                      : {};
                return (
                  <HeaderColumn key={col.key} {...alignProp}>
                    {renderHeaderCell(col)}
                  </HeaderColumn>
                );
              })}
            </TableHeader>
            <tbody>
              {isLoading ? (
                <OrderSkeleton rowsCount={pageSize} />
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableData
                    colSpan={ORDER_COLUMNS.length + 1}
                    style={{ textAlign: "center", padding: "48px", color: "#686978" }}
                  >
                    No orders found.
                  </TableData>
                </TableRow>
              ) : (
                orders.map(order => (
                  <TableRow key={order.id}>
                    <TableData style={{ color: "#1249bf", fontWeight: 500 }}>
                      {order.sales_no}
                    </TableData>
                    <TableData>
                      {order.order_date
                        ? new Date(order.order_date).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "N/A"}
                    </TableData>
                    <TableData>{order.po_ref || "--"}</TableData>
                    <TableData>{order.product_code}</TableData>
                    <TableData>{order.product_name}</TableData>
                    <TableData rightAlign>
                      {currencySymbol}
                      {formatAmount(order.sub_total || 0)}
                    </TableData>
                    <TableData rightAlign>
                      {currencySymbol}
                      {formatAmount(order.tax || 0)}
                    </TableData>
                    <TableData rightAlign>
                      {currencySymbol}
                      {formatAmount(order.total || 0)}
                    </TableData>
                    <TableData centerAlign>
                      <Status statusName={formatStatus(order.status)} />
                    </TableData>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </TableCard>

        {/* Pagination at the bottom right */}
        <PaginationWrapper>
          <Pagination
            current={currentPage}
            total={totalCount}
            showSizeChanger
            showQuickJumper
            pageSize={pageSize}
            onChange={handlePageChange}
            showTotal={(total, range) => `Results ${range[0]}-${range[1]} of ${total}`}
            locale={{
              jump_to: "Go to",
              page: "Page",
            }}
            disabled={isLoading}
          />
        </PaginationWrapper>

        {/* Footer */}
        <FooterWrapper>
          <B2bFooter />
        </FooterWrapper>
      </StyledSectionContainerDiv>
    </>
  );
};
